import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../api";
import { ACTIVITY_TYPES, SKILLS, label } from "../../labels";
import { btnPrimary, btnSage, inputCls, panel } from "./ui";

const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({
  date: today(), activityType: "test", skill: "listening", criterion: "", score: "", qualitative: "",
  comment: "", achievements: "", toImprove: "", internalNote: "", teacherName: "",
});

function Field({ title, hint, children }) {
  return (
    <label className="block text-[11px] font-semibold text-[#132C44]">
      {title}
      {hint && <span className="font-normal text-[#132C44]/55"> · {hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

export default function AssessmentForm({ student, initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial ? { ...blank(), ...initial, score: initial.score ?? "" } : blank());
  const [scale, setScale] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings").then(({ data }) => setScale(data.qualitativeScale));
  }, []);
  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (status) => {
    setErr("");
    if (!form.criterion.trim()) return setErr("Indicá los criterios evaluados.");
    setBusy(true);
    const body = {
      date: form.date, activityType: form.activityType, skill: form.skill, criterion: form.criterion,
      qualitative: form.qualitative || null, comment: form.comment || null, achievements: form.achievements || null,
      toImprove: form.toImprove || null, internalNote: form.internalNote || null, teacherName: form.teacherName || null,
    };
    if (form.score !== "") body.score = Number(form.score);
    if (status) body.status = status;
    try {
      if (initial) {
        if (form.score === "" && initial.score != null) body.clearScore = true;
        await api.patch(`/assessments/${initial.id}`, body);
      } else {
        await api.post("/assessments", { ...body, studentId: student.id });
        setForm(blank());
      }
      onSaved();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${panel} space-y-4`} data-testid="eid-asm-form">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field title="Fecha"><input type="date" className={inputCls} value={form.date} onChange={f("date")} data-testid="eid-asm-date" /></Field>
        <Field title="Tipo de actividad">
          <select className={inputCls} value={form.activityType} onChange={f("activityType")} data-testid="eid-asm-type">
            {ACTIVITY_TYPES.map((t) => <option key={t} value={t}>{label(t)}</option>)}
          </select>
        </Field>
        <Field title="Habilidad">
          <select className={inputCls} value={form.skill} onChange={f("skill")} data-testid="eid-asm-skill">
            {SKILLS.map((s) => <option key={s} value={s}>{label(s)}</option>)}
          </select>
        </Field>
        <Field title="Calificación" hint={`escala ${student.scaleMin}–${student.scaleMax}`}>
          <input type="number" step="0.1" min={student.scaleMin} max={student.scaleMax} className={inputCls} value={form.score} onChange={f("score")} placeholder="Opcional" data-testid="eid-asm-score" />
        </Field>
      </div>
      <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
        <Field title="Criterios evaluados"><input className={inputCls} value={form.criterion} onChange={f("criterion")} maxLength={120} placeholder="Comprensión de consignas orales" data-testid="eid-asm-criterion" /></Field>
        <Field title="Valoración cualitativa" hint="independiente de la nota">
          <select className={inputCls} value={form.qualitative || ""} onChange={f("qualitative")} data-testid="eid-asm-qualitative">
            <option value="">Sin valoración</option>
            {scale.map((v) => <option key={v} value={v}>{v}</option>)}
            {form.qualitative && !scale.includes(form.qualitative) && <option value={form.qualitative}>{form.qualitative}</option>}
          </select>
        </Field>
      </div>
      <Field title="Comentario para el alumno y la familia"><textarea rows={2} className={inputCls} value={form.comment || ""} onChange={f("comment")} maxLength={1000} data-testid="eid-asm-comment" /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field title="Objetivos alcanzados"><textarea rows={2} className={inputCls} value={form.achievements || ""} onChange={f("achievements")} maxLength={1000} data-testid="eid-asm-achievements" /></Field>
        <Field title="Aspectos a reforzar"><textarea rows={2} className={inputCls} value={form.toImprove || ""} onChange={f("toImprove")} maxLength={1000} data-testid="eid-asm-toimprove" /></Field>
      </div>
      <div className="grid sm:grid-cols-[2fr_1fr] gap-3">
        <Field title="Observación interna" hint="solo administración, nunca visible para alumno o familia">
          <textarea rows={2} className={`${inputCls} bg-[#F6EFDF]/60`} value={form.internalNote || ""} onChange={f("internalNote")} maxLength={1000} data-testid="eid-asm-internal" />
        </Field>
        <Field title="Docente"><input className={inputCls} value={form.teacherName || ""} onChange={f("teacherName")} maxLength={80} data-testid="eid-asm-teacher" /></Field>
      </div>
      {err && <div className="text-[12px] text-[#9B2C3B]" data-testid="eid-asm-error">{err}</div>}
      <div className="flex gap-2 flex-wrap">
        {initial ? (
          <>
            <button disabled={busy} onClick={() => submit()} className={btnPrimary} data-testid="eid-asm-save">Guardar cambios</button>
            <button onClick={onCancel} className="text-[12px] px-3 hover:underline">Cancelar</button>
          </>
        ) : (
          <>
            <button disabled={busy} onClick={() => submit("draft")} className={btnPrimary} data-testid="eid-asm-save-draft">Guardar borrador</button>
            <button disabled={busy} onClick={() => submit("published")} className={btnSage} data-testid="eid-asm-save-publish">Guardar y publicar</button>
          </>
        )}
      </div>
    </div>
  );
}
